// ═══════════════════════════════════════════════════════════════════
// Nhãn + màu trạng thái đơn hàng — dùng chung cho apps/web và apps/admin.
//
// File THUẦN, không import gì (orderStateMachine.ts kéo errors.ts, không đưa
// vào bundle trình duyệt). Danh sách trạng thái chuẩn vẫn là ORDER_STATUSES ở
// orderStateMachine.ts — thêm trạng thái mới ở đó thì bổ sung nhãn tại đây.
// ═══════════════════════════════════════════════════════════════════

export type StatusIntent = 'ok' | 'warn' | 'danger' | 'info' | 'ship' | 'neutral'

export interface StatusMeta {
  text: string
  intent: StatusIntent
}

export const STATUS_LABEL: Record<string, StatusMeta> = {
  // ─── Trạng thái chuẩn (ORDER_STATUSES) ───────────────────────
  // Hẹn gửi: tới giờ hẹn mới gửi hãng / gửi duyệt ship
  'hen-gio': { text: 'Hẹn gửi', intent: 'info' },
  // Shop trả ship: chờ ShopTrack xác nhận chuyển khoản rồi mới gửi BEST
  'cho-duyet': { text: 'Chờ duyệt ship', intent: 'warn' },
  'cho-lay': { text: 'Chờ lấy', intent: 'info' },
  // Có màu riêng: đây là trạng thái nhiều đơn nhất, không được trông giống đơn đã hủy.
  'dang-giao': { text: 'Đang giao', intent: 'ship' },
  'giao-ok': { text: 'Đã giao', intent: 'ok' },
  hoan: { text: 'Hoàn', intent: 'warn' },
  huy: { text: 'Đã hủy', intent: 'neutral' },
  // Hàng ĐÃ lên đường rồi gặp sự cố (BEST 777 thất lạc / 1000 hư hỏng).
  failed_shipment: { text: 'Sự cố giao hàng', intent: 'danger' },
  // Gọi API hãng lỗi — vận đơn CHƯA được tạo, hàng còn ở shop, tạo lại được.
  draft_failed: { text: 'Lỗi tạo vận đơn', intent: 'danger' },

  // ─── Nhãn cũ / nguồn ngoài — giữ để không vỡ dữ liệu lịch sử ──
  cancelling: { text: 'Đang hủy', intent: 'warn' },
  cancelled: { text: 'Đã hủy', intent: 'neutral' },
  'da-huy': { text: 'Đã hủy', intent: 'neutral' },
  pending: { text: 'Chờ xử lý', intent: 'info' },
  processing: { text: 'Đang xử lý', intent: 'info' },
  delivered: { text: 'Đã giao', intent: 'ok' },
  returned: { text: 'Đã hoàn', intent: 'warn' },
}

export function statusMeta(status?: string | null): StatusMeta {
  if (status && STATUS_LABEL[status]) return STATUS_LABEL[status]
  return { text: status || '—', intent: 'neutral' }
}

// ─── Nhóm trạng thái của sổ tiền (OrderFinance.status) ────────────
// Bảng "Đơn hàng" của admin đếm theo sổ tiền ở control DB (một câu groupBy cho
// mọi đơn vị) — mỗi đơn có vận đơn là một dòng.

export type OrderGroup = 'processing' | 'delivered' | 'returned' | 'cancelled'

export const ORDER_GROUPS: readonly OrderGroup[] = ['processing', 'delivered', 'returned', 'cancelled']

export const ORDER_GROUP_LABEL: Record<OrderGroup, string> = {
  processing: 'Đang xử lý',
  delivered: 'Thành công',
  returned: 'Hoàn',
  cancelled: 'Hủy',
}

/**
 * Nhóm của trạng thái đơn trong DB shop (Order.status) — cùng thước với financeStatusGroup để đối chiếu đơn ↔ sổ
 * (job finance assert). Đơn chưa có trạng thái → null.
 */
export function tenantStatusGroup(status: string | null | undefined): OrderGroup | null {
  switch (status) {
    case null:
    case undefined:
    case '':
      return null
    case 'giao-ok': return 'delivered'
    case 'hoan':
    case 'failed_shipment': return 'returned'
    case 'huy':
    case 'draft_failed': return 'cancelled'
    default: return 'processing'
  }
}

/** draft (chưa có vận đơn) → null: không đếm. Thất lạc tính chung với hoàn. */
export function financeStatusGroup(status: string | null | undefined): OrderGroup | null {
  switch (status) {
    case 'open': return 'processing'
    case 'delivered': return 'delivered'
    case 'returned':
    case 'lost': return 'returned'
    case 'cancelled': return 'cancelled'
    default: return null
  }
}
